import { useEffect, useState } from "react"
import ReactGA from "react-ga4"
import { Navigate, Route, Routes, useLocation } from "react-router-dom"
import Header from "./components/common/Header"
import Footer from "./components/common/Footer"
import Loading from "./components/common/Loading"
import BaseLayout from "./components/layout/BaseLayout"
import Homepage from "./pages/Homepage/Homepage"
import About from "./pages/About/About"
import Impressum from "./pages/common/Impressum"
import Datenschutz from "./pages/common/Datenschutz"
import ErrorPage from "./pages/404/ErrorPage"
import Portfolio from "./pages/Portfolio/Portfolio"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import { useGSAP } from "@gsap/react"
import gsap from "gsap"
import Cookiebox from "./components/common/Cookiebox"
import FlyingCursor from "./components/common/FlyingCursor"
import Contact from "./pages/Contact/Contact"
import ScrollToHashElement from "./components/common/SmoothScroll"
import ProjectDetailPage from "./pages/ProjectDetail/ProjectDetailPage"
import AGB from "./pages/common/AGB"
import PersonPage from "./pages/networking/PersonPage"
import { PopupButton } from "react-calendly"
import { ReactComponent as CALENDAR } from "./assets/Calendar.svg"
import { COOKIE, GA_TRACKING_ID } from "./api/api"
import { useQuery } from "@tanstack/react-query"
import { getSeoData } from "./api/apiCalls"
import Jobs from "./pages/Jobs/Jobs"
import IndustryDetailPage from "./pages/IndustryDetail/IndustryDetailPage"
import SolutionDetail from "./pages/SolutionDetail/SolutionDetail"

gsap.registerPlugin(useGSAP, ScrollTrigger)

function App() {
    const [loading, setLoading] = useState(true)
    const [visible, setVisible] = useState(false)
    const [presence, setPresence] = useState(false)

    const handleLoading = () => {
        setLoading(false)
    }

    // this hook is just nessecary as long as there are no loading conditions like axios.get
    useEffect(() => {
        setTimeout(() => {
            handleLoading()
        }, 300)
    })

    useEffect(() => {
        if (localStorage.getItem(COOKIE.KEY) === null) {
            setPresence(true)
            setVisible(true)
        } else if (localStorage.getItem(COOKIE.KEY)) {
            enableAnalytics()
        }
    }, [])

    const handleAcceptAll = () => {
        localStorage.setItem(COOKIE.KEY, "true") // das anpassen!
        setVisible(!visible)
        setTimeout(() => {
            setPresence(!presence)
        }, 550)
        enableAnalytics()
    }

    const handleAcceptNecessary = () => {
        setVisible(!visible)
        setTimeout(() => {
            setPresence(!presence)
        }, 550)
    }

    // enables Google Analytics
    // hier ANPASSEN
    const enableAnalytics = () => {
        ReactGA.initialize(GA_TRACKING_ID)
    }

    const [scrollProgress, setScrollProgress] = useState<number>(0)
    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.scrollY // Get scroll position from window
            const maxScroll = document.documentElement.scrollHeight - window.innerHeight
            const progress = maxScroll ? scrollTop / maxScroll : 0
            setScrollProgress(progress)
        }

        window.addEventListener("scroll", handleScroll, { passive: true })

        return () => {
            window.removeEventListener("scroll", handleScroll)
        }
    }, [])

    // CHANGE PAGE ANIMATION
    const location = useLocation()

    // SEO
    const { data } = useQuery({
        queryKey: ["seo"],
        queryFn: () => getSeoData(),
    })

    return (
        <main className="relative h-screen">
            <FlyingCursor />
            <ScrollToHashElement />
            {loading && <Loading />}

            {presence && !loading && <Cookiebox visible={visible} handleAcceptNecessary={handleAcceptNecessary} handleAcceptAll={handleAcceptAll} />}
            {!loading && data && (
                <div className={"flex flex-col w-full min-h-screen "}>
                    <button className="p-4 rounded-full text-accent bg-primary fixed bottom-8 right-4 sm:right-8 md:bottom-16 md:right-16 z-[1000]">
                        <PopupButton
                            url={"https://calendly.com/niklas-wicode/30min"}
                            text={""}
                            rootElement={document.getElementById("root") as HTMLElement}
                            className="w-full h-full absolute"
                        />
                        <CALENDAR className="w-6 h-6" />
                    </button>
                    <Header />
                    <Routes location={location} key={location.pathname}>
                        <Route
                            path="/"
                            element={
                                <BaseLayout seo={data?.homepage} children={<Homepage loadingFunc={handleLoading} scrollPositionY={scrollProgress} />} />
                            }
                        />
                        <Route path="/about" element={<BaseLayout seo={data?.about} children={<About />} />} />
                        <Route path="/portfolio" element={<BaseLayout seo={data?.portfolio} children={<Portfolio />} />} />
                        <Route path="/portfolio/:id" element={<BaseLayout seo={{ metaTitle: "Wicode | Projekt", metaRobots: "noindex, nofollow, disallow" }} children={<ProjectDetailPage />} />} />
                        <Route path="/branche/:id" element={<BaseLayout seo={{ metaTitle: "Branche", metaRobots: "noindex, nofollow, disallow" }} children={<IndustryDetailPage />} />} />
                        <Route path="/software/:id" element={<BaseLayout seo={{ metaTitle: "Softwarelösungen", metaRobots: "noindex, nofollow, disallow" }} children={<SolutionDetail />} />} />
                        <Route path="/contact" element={<BaseLayout seo={{ metaTitle: "Kontakt", metaRobots: "index, follow, allow" }} children={<Contact />} />} />
                        <Route path="/impressum" element={<BaseLayout seo={{ metaTitle: "Impressum", metaRobots: "noindex, nofollow, disallow" }} children={<Impressum />} />} />
                        <Route path="/datenschutz" element={<BaseLayout seo={{ metaTitle: "Datenschutz", metaRobots: "noindex, nofollow, disallow" }} children={<Datenschutz />} />} />
                        <Route path="/agb" element={<BaseLayout seo={{ metaTitle: "AGB", metaRobots: "noindex, nofollow, disallow" }} children={<AGB />} />} />
                        <Route path="/jobs" element={<BaseLayout seo={{ metaTitle: "Jobs", metaRobots: "noindex, nofollow, disallow" }} children={<Jobs />} />} />
                        <Route path="/niklas" element={<BaseLayout seo={{ metaTitle: "Niklas", metaRobots: "noindex, nofollow, disallow" }} children={<PersonPage />} />} />
                        <Route path="/felix" element={<BaseLayout seo={{ metaTitle: "Felix", metaRobots: "noindex, nofollow, disallow" }} children={<PersonPage />} />} />
                        <Route path="/404" element={<BaseLayout seo={{ metaTitle: "Seite nicht gefunden", metaRobots: "noindex, nofollow, disallow" }} children={<ErrorPage />} />} />
                        <Route path="*" element={<Navigate to="/404" />} />
                    </Routes>
                    <Footer />
                </div>
            )}
        </main>
    )
}

export default App
